import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { 
  IoPersonOutline, 
  IoSearchOutline,
  IoShieldCheckmarkOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoTrophyOutline,
  IoBookOutline,
  IoSettingsOutline,
  IoCalendarOutline,
  IoEyeOutline,
  IoChevronDownOutline,
  IoChevronUpOutline
} from 'react-icons/io5';
import './AdminUsersPage.css';

const AdminUsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  
  // Filters and search
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Expanded user details
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    if (user?.isAdmin) {
      loadUsers();
    }
  }, [user, currentPage, search, sortBy, sortOrder]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/admin/users', {
        params: {
          page: currentPage,
          limit: 20,
          search,
          sortBy,
          sortOrder
        }
      });
      
      setUsers(response.data.users || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Load users error:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleExpandUser = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-access-denied">
        <h1>Access Denied</h1>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading && users.length === 0) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading users..." />
      </div>
    );
  }

  return (
    <div className="admin-users-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">User Management</h1>
        <p className="admin-page-description">
          View and manage all users, their settings, and preferences
        </p>
      </div>

      {/* Search and Filters */}
      <div className="admin-card">
        <div className="users-controls">
          <div className="search-box">
            <IoSearchOutline className="search-icon" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          
          <div className="sort-controls">
            <label>Sort by:</label>
            <select 
              value={sortBy} 
              onChange={(e) => handleSortChange(e.target.value)}
              className="sort-select"
            >
              <option value="createdAt">Date Joined</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="lastLogin">Last Login</option>
              <option value="competitiveMode">Competitive Mode</option>
              <option value="bibleTranslation">Bible Translation</option>
            </select>
            <button 
              onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
              className="sort-order-btn"
            >
              {sortOrder === 'ASC' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            Users ({pagination.totalUsers || 0})
          </h2>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={loadUsers} className="retry-btn">
              Try Again
            </button>
          </div>
        )}

        {users.length === 0 && !loading ? (
          <div className="no-users">
            <IoPersonOutline className="no-users-icon" />
            <h3>No users found</h3>
            <p>No users match your search criteria.</p>
          </div>
        ) : (
          <div className="users-list">
            {users.map((userData) => (
              <div key={userData.id} className="user-item">
                <div className="user-header" onClick={() => handleExpandUser(userData.id)}>
                  <div className="user-info">
                    <div className="user-name-section">
                      <h3 className="user-name">{userData.name}</h3>
                      <div className="user-badges">
                        {userData.isAdmin && (
                          <span className="admin-badge">
                            <IoShieldCheckmarkOutline /> Admin
                          </span>
                        )}
                        {userData.emailVerified ? (
                          <span className="verified-badge">
                            <IoCheckmarkCircleOutline /> Verified
                          </span>
                        ) : (
                          <span className="unverified-badge">
                            <IoCloseCircleOutline /> Unverified
                          </span>
                        )}
                        {userData.competitiveMode && (
                          <span className="competitive-badge">
                            <IoTrophyOutline /> Competitive
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="user-meta">
                      <span className="user-email">{userData.email}</span>
                      <div className="user-stats">
                        <span className="user-stat">
                          <IoPersonOutline /> {userData.totalReaders} readers
                        </span>
                        <span className="user-stat">
                          <IoBookOutline /> {userData.totalReports} reports
                        </span>
                        <span className="user-stat">
                          <IoCalendarOutline /> Joined {formatDate(userData.createdAt)}
                        </span>
                        <span className="user-stat">
                          Last login: {formatDate(userData.lastLogin)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="expand-btn">
                    {expandedUser === userData.id ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                  </div>
                </div>

                {expandedUser === userData.id && (
                  <div className="user-details">
                    <UserDetailsView user={userData} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const UserDetailsView = ({ user }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not set';
    return value;
  };

  return (
    <div className="user-details-content">
      <div className="details-section">
        <h4><IoPersonOutline /> Personal Information</h4>
        <div className="details-grid">
          <div className="detail-item">
            <label>Full Name:</label>
            <span>{user.name}</span>
          </div>
          <div className="detail-item">
            <label>Email:</label>
            <span>{user.email}</span>
          </div>
          <div className="detail-item">
            <label>Age:</label>
            <span>{formatValue(user.age)}</span>
          </div>
          <div className="detail-item">
            <label>Gender:</label>
            <span>{formatValue(user.gender)}</span>
          </div>
          <div className="detail-item">
            <label>Account Status:</label>
            <span className={user.emailVerified ? 'status-verified' : 'status-unverified'}>
              {user.emailVerified ? 'Verified' : 'Unverified'}
            </span>
          </div>
          <div className="detail-item">
            <label>Admin Status:</label>
            <span className={user.isAdmin ? 'status-admin' : 'status-regular'}>
              {user.isAdmin ? 'Administrator' : 'Regular User'}
            </span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h4><IoSettingsOutline /> Preferences & Settings</h4>
        <div className="details-grid">
          <div className="detail-item">
            <label>Bible Translation:</label>
            <span>{formatValue(user.bibleTranslation)}</span>
          </div>
          <div className="detail-item">
            <label>Competitive Mode:</label>
            <span className={user.competitiveMode ? 'status-enabled' : 'status-disabled'}>
              {user.competitiveMode ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="detail-item">
            <label>App Usage Mode:</label>
            <span>{formatValue(user.appUsageMode)}</span>
          </div>
          <div className="detail-item">
            <label>Default Reading Method:</label>
            <span>{formatValue(user.defaultReadingMethod)}</span>
          </div>
          <div className="detail-item">
            <label>Default Audio Speed:</label>
            <span>{user.defaultAudioSpeed ? `${user.defaultAudioSpeed}x` : 'Not set'}</span>
          </div>
          <div className="detail-item">
            <label>Default Location:</label>
            <span>{formatValue(user.defaultLocation)}</span>
          </div>
          <div className="detail-item">
            <label>Default Time of Day:</label>
            <span>{formatValue(user.defaultTimeOfDay)}</span>
          </div>
          <div className="detail-item">
            <label>Track Reading Details:</label>
            <span className={user.trackReadingDetails ? 'status-enabled' : 'status-disabled'}>
              {user.trackReadingDetails ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h4><IoCalendarOutline /> Account Timeline</h4>
        <div className="details-grid">
          <div className="detail-item">
            <label>Account Created:</label>
            <span>{formatDate(user.createdAt)}</span>
          </div>
          <div className="detail-item">
            <label>Last Login:</label>
            <span>{formatDate(user.lastLogin)}</span>
          </div>
          <div className="detail-item">
            <label>Onboarding Completed:</label>
            <span className={user.onboardingCompleted ? 'status-completed' : 'status-pending'}>
              {user.onboardingCompleted ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="detail-item">
            <label>Onboarding Completed At:</label>
            <span>{formatDate(user.onboardingCompletedAt)}</span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h4><IoPersonOutline /> Readers & Activity</h4>
        <div className="readers-summary">
          <div className="summary-stat">
            <label>Total Readers:</label>
            <span className="stat-value">{user.totalReaders}</span>
          </div>
          <div className="summary-stat">
            <label>Total Reports:</label>
            <span className="stat-value">{user.totalReports}</span>
          </div>
          <div className="summary-stat">
            <label>Family Groups:</label>
            <span className="stat-value">{user.totalFamilies}</span>
          </div>
        </div>

        {user.readers && user.readers.length > 0 && (
          <div className="readers-list">
            <h5>Readers:</h5>
            {user.readers.map((reader) => (
              <div key={reader.id} className="reader-item">
                <div className="reader-info">
                  <strong>{reader.name}</strong>
                  {reader.isPrimary && <span className="primary-badge">Primary</span>}
                </div>
                <div className="reader-stats">
                  <span className="reader-stat">
                    Reports: {reader.reports?.length || 0}
                  </span>
                  <span className={`reader-status ${reader.isActive ? 'active' : 'inactive'}`}>
                    {reader.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {user.families && user.families.length > 0 && (
          <div className="families-list">
            <h5>Family Groups:</h5>
            {user.families.map((family) => (
              <div key={family.id} className="family-item">
                <strong>{family.name}</strong>
                <span className="family-code">({family.inviteCode})</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
